import React, { useEffect, useRef, useState } from "react";
import { City } from "../../services/searchService";

interface CitySelectProps {
  cities: City[];
  value: City | null;
  onSelect: (city: City) => void;
  placeholder?: string;
  className?: string;
}

// A plain city picker (no Google Places involved) - used for the Airport
// side of a trip, where the location must be a real City matching a Plan
// (e.g. "Hyderabad"), not a free-form address. Filters the given city list
// by name as the user types and lets them pick one from a dropdown.
const CitySelect: React.FC<CitySelectProps> = ({
  cities,
  value,
  onSelect,
  placeholder,
  className,
}) => {
  const [query, setQuery] = useState(value?.name ?? "");
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setQuery(value?.name ?? "");
  }, [value]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
        setQuery(value?.name ?? "");
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [value]);

  const matches = cities
    .filter((city) =>
      city.name.toLowerCase().includes(query.trim().toLowerCase())
    )
    .slice(0, 8);

  const handleSelect = (city: City) => {
    onSelect(city);
    setQuery(city.name);
    setIsOpen(false);
  };

  return (
    <div className="relative w-full" ref={containerRef}>
      <input
        type="text"
        className={className}
        placeholder={placeholder}
        value={query}
        onFocus={() => setIsOpen(true)}
        onChange={(e) => {
          setQuery(e.target.value);
          setIsOpen(true);
        }}
      />
      {isOpen && matches.length > 0 && (
        <ul className="absolute left-0 right-0 top-full mt-1 max-h-56 overflow-y-auto bg-white border rounded-md shadow-lg z-20">
          {matches.map((city) => (
            <li key={city.id}>
              <button
                type="button"
                className="w-full text-left px-3 py-2 hover:bg-gray-100 text-sm text-gray-700"
                onClick={() => handleSelect(city)}
              >
                {city.name}
                {city.state ? `, ${city.state}` : ""}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default CitySelect;
